// Office "Office users" page (admin only, Session AAQ Loop 3). Manages the office + admin accounts.
// Two ways in, reconciled from two decisions taken the same day: an admin can create a direct email +
// password account (Director decision, 26 September 2026: office/admin accounts are no longer
// Google-only), or invite someone to sign in with Google (Session ASG, D120: a Google sign-in with no
// account is refused, never auto-created, so an admin adds the row here first and the person's first
// "Sign in with Google" adopts it). Either way an admin can set or reset a password on any account,
// deactivate one (soft + reversible: the person is locked out on their next request, but the record
// and its history are kept) or delete it permanently. Your own account and the last active
// administrator are protected.
// Engineers and external auditors keep their own screens.
//
// SESSION AUV, design system Session 8 (People): the page now speaks the shared design system. PageHeader, a NEW
// ListToolbar (search over name, email, role and status, a result count, the below-md sort and Clear), sortable
// headers on the table, StatusPill for Active / Deactivated (the same green and red as the coloured words they
// replace), Button for the primary actions, Loader and EmptyState. The neutral outline row actions (Set password,
// Deactivate, Reactivate, Delete) are left as they are: the Button component has no neutral variant. Presentation
// only: every route, call, permission and word of copy is as before.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "../../lib/api";
import type { OfficeUser } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
// Session ASB: "Sign out everywhere" for one person (ends their sessions, keeps the account).
import { SignOutEverywhereButton } from "./SignOutEverywhereButton";
import { ConfirmButton } from "../../components/ConfirmButton";
import { friendlyApiError } from "../../lib/friendlyApiError";

// Search and sort run client-side: the list is the whole set of office and admin accounts, never paginated.
const statusWord = (u: OfficeUser): string => (u.isActive ? "Active" : "Deactivated");
const USER_SEARCH: SearchAccessors<OfficeUser> = [(u) => u.displayName, (u) => u.email, (u) => u.role, statusWord];
const USER_SORT: SortAccessors<OfficeUser> = {
  name: (u) => u.displayName,
  email: (u) => u.email,
  role: (u) => u.role,
  status: (u) => (u.isActive ? 0 : 1),
};
const SORT_OPTIONS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "status", label: "Status" },
];
const statusTag = (u: OfficeUser) => <StatusPill word={statusWord(u)} tone={u.isActive ? "green" : "red"} />;

// Session ASG (D120): the invite. Google sign-in never creates an account, so an admin adds the person here first
// (or, since Session ASJ, creates a password account with the form above). The person then signs in with Google using
// the same address and the account is theirs. Two-step confirm naming the person and the role.
function AddOfficeUserForm({ onAdded, onCancel }: { onAdded: (u: OfficeUser) => void; onCancel: () => void }) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"office" | "admin">("office");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = displayName.trim().length > 0 && email.trim().length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      onAdded(await api.admin.createOfficeUser({ email: email.trim(), displayName: displayName.trim(), role }));
    } catch (err) {
      setError(friendlyApiError(err, "Could not add that person. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="font-heading text-xl tracking-wide">Add office user</h2>
      <p className="mt-1 text-sm text-ele-body">
        They sign in with Google using this address. Nobody can sign in to the office until an administrator adds them here.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="text-xs text-ele-body">
          Name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
            className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-inset px-2 py-1.5 text-sm text-ele-strong"
          />
        </label>
        <label className="text-xs text-ele-body">
          Google email address
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@eastlondonelevators.co.uk"
            maxLength={254}
            className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-inset px-2 py-1.5 text-sm text-ele-strong"
          />
        </label>
        <label className="text-xs text-ele-body">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value === "admin" ? "admin" : "office")}
            className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-inset px-2 py-1.5 text-sm text-ele-strong"
          >
            <option value="office">Office</option>
            <option value="admin">Admin (full control, including these accounts)</option>
          </select>
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <ConfirmButton
          onConfirm={() => void submit()}
          label="Add office user"
          confirmLabel={`Add ${displayName.trim() || "this person"} as ${role}?`}
          busy={busy}
          busyLabel="Adding..."
          disabled={!ready}
          className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-sm text-ele-strong transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
        />
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// A pending confirm action (deactivate = amber/reversible, delete = red/permanent).
type Pending = { user: OfficeUser; action: "deactivate" | "delete" };

// Card-only actions (Session ALY mobile card conversion). The desktop cell below hides Delete
// below md (max-md:hidden) because the fixed-layout table has no room for a third control; the
// card stacks instead of dividing width, so Delete comes back here rather than staying dropped.
// Returns null for the acting admin's own row, matching the desktop's "no actions on yourself"
// rule, so the card renders with no actions bar at all rather than a lone dash.
function officeUserCardActions(
  u: OfficeUser,
  busyId: string | null,
  onDeactivate: (u: OfficeUser) => void,
  onReactivate: (u: OfficeUser) => void,
  onDelete: (u: OfficeUser) => void,
) {
  if (u.isSelf) return null;
  return (
    <>
      {u.isActive ? (
        <button
          type="button"
          disabled={busyId === u.id}
          onClick={() => onDeactivate(u)}
          className="min-h-11 flex-1 rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-amber-500 hover:text-amber-400 disabled:opacity-50"
        >
          Deactivate
        </button>
      ) : (
        <button
          type="button"
          disabled={busyId === u.id}
          onClick={() => onReactivate(u)}
          className="min-h-11 flex-1 rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
        >
          {busyId === u.id ? "..." : "Reactivate"}
        </button>
      )}
      <button
        type="button"
        disabled={busyId === u.id}
        onClick={() => onDelete(u)}
        className="min-h-11 flex-1 rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger disabled:opacity-50"
      >
        Delete
      </button>
    </>
  );
}

// Create a new office/admin account with an email + password login (Director decision, 26 September
// 2026). Google sign-in remains available separately for anyone who uses it.
function CreateOfficeUserForm({ onCreated }: { onCreated: (u: OfficeUser) => void }) {
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"office" | "admin">("office");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function reset() {
    setDisplayName("");
    setEmail("");
    setRole("office");
    setPassword("");
    setConfirmPassword("");
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await api.admin.createOfficeUser({ displayName: displayName.trim(), email: email.trim(), role, password, confirmPassword });
      onCreated(created);
      reset();
      setOpen(false);
    } catch (err) {
      setError(friendlyApiError(err, "Could not create that account. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Button variant="secondary" size="sm" className="mb-4" onClick={() => setOpen(true)}>
        + Create office user
      </Button>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <p className="mb-3 font-menu text-xs uppercase tracking-widest text-ele-blue">New office / admin account</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm" htmlFor="new-office-name">Name</label>
          <input
            id="new-office-name"
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm" htmlFor="new-office-email">Email</label>
          <input
            id="new-office-email"
            type="text"
            autoCapitalize="none"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm" htmlFor="new-office-role">Role</label>
          <select
            id="new-office-role"
            value={role}
            onChange={(e) => setRole(e.target.value as "office" | "admin")}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          >
            <option value="office">Office (full access)</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div />
        <div>
          <label className="mb-1 block text-sm" htmlFor="new-office-password">Password</label>
          <input
            id="new-office-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm" htmlFor="new-office-confirm">Confirm password</label>
          <input
            id="new-office-confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-black/20 px-3 py-2 text-sm text-ele-white">
          {error}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? "Creating..." : "Create account"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// What the office is told after a new password is saved. The server ends every session the person holds (Session ASL),
// except the one an admin is using to change their OWN password, so the sentence says which happened.
export function passwordSetMessage(u: Pick<OfficeUser, "displayName" | "isSelf">): string {
  return u.isSelf
    ? "Your password was set. You stay signed in here; any other device you were signed in on has been signed out."
    : `${u.displayName}'s password was set. They have been signed out on every device and sign in with the new password.`;
}

// Set or reset the password on an existing account: inline, small, per row. Gives a Google-only
// account a password login, or lets the office reset one that has forgotten theirs.
function SetPasswordForm({ user, onDone, onCancel }: { user: OfficeUser; onDone: () => void; onCancel: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.admin.setOfficeUserPassword(user.id, { password, confirmPassword });
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "Could not set that password. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-2 flex flex-wrap items-end gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <div>
        <label className="mb-1 block text-xs" htmlFor={`pw-${user.id}`}>New password</label>
        <input
          id={`pw-${user.id}`}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm outline-none focus:border-ele-green"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs" htmlFor={`pw-confirm-${user.id}`}>Confirm</label>
        <input
          id={`pw-confirm-${user.id}`}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm outline-none focus:border-ele-green"
        />
      </div>
      <Button type="submit" size="sm" disabled={busy}>
        {busy ? "Saving..." : "Save"}
      </Button>
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      {error && <p role="alert" className="w-full text-xs text-ele-danger">{error}</p>}
    </form>
  );
}

export function OfficeUsersPage() {
  const [users, setUsers] = useState<OfficeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const [pending, setPending] = useState<Pending | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [settingPasswordFor, setSettingPasswordFor] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, the
  // InvoicesPage precedent). jsdom has no matchMedia, so every existing test still renders the
  // table it pins.
  const [mobileCards] = useState(initialMobileLayout);
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("office-users");

  const load = useCallback(async () => {
    setLoading(true);
    setListError(false);
    try {
      setUsers(await api.admin.listOfficeUsers());
    } catch {
      setListError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Plain-English message for a failed action (never a raw machine code).
  function actionErrorMessage(err: unknown): string {
    if (err instanceof ApiError && (err.status === 400 || err.status === 409) && err.message) return err.message;
    return "Could not complete that change. Please try again.";
  }


  async function reactivate(user: OfficeUser) {
    setBusyId(user.id);
    setBanner(null);
    try {
      const updated = await api.admin.reactivateOfficeUser(user.id);
      setUsers((xs) => xs.map((x) => (x.id === user.id ? updated : x)));
      setBanner(`${updated.displayName} can sign in again.`);
    } catch (err) {
      setBanner(actionErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmPending() {
    if (!pending) return;
    const { user, action } = pending;
    setBusyId(user.id);
    setModalError(null);
    try {
      if (action === "delete") {
        await api.admin.deleteOfficeUser(user.id);
        setUsers((xs) => xs.filter((x) => x.id !== user.id));
        setBanner(`${user.displayName} was deleted.`);
      } else {
        const updated = await api.admin.deactivateOfficeUser(user.id);
        setUsers((xs) => xs.map((x) => (x.id === user.id ? updated : x)));
        setBanner(`${updated.displayName} was deactivated and can no longer sign in.`);
      }
      setPending(null);
    } catch (err) {
      setModalError(actionErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  const visible = useMemo(() => sortRows(filterRows(users, search, USER_SEARCH), USER_SORT, sort), [users, search, sort.sortKey, sort.sortDir]);

  function openDeactivate(user: OfficeUser) {
    setModalError(null);
    setPending({ user, action: "deactivate" });
  }

  function openDelete(user: OfficeUser) {
    setModalError(null);
    setPending({ user, action: "delete" });
  }

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Office users"
        subtitle="The office and admin accounts. Create one with an email and password directly below, or invite someone to sign in with Google using their @ele address (a Google sign-in with no account is refused, never auto-created). Deactivate an account when someone leaves (they are locked out immediately; their record and history are kept) or delete it permanently. You cannot deactivate or delete your own account or the last active administrator. Manage engineers and external auditors from their own screens."
        actions={
          !adding ? (
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              Invite Google sign-in
            </Button>
          ) : undefined
        }
      />

      <CreateOfficeUserForm onCreated={(u) => { setUsers((xs) => [u, ...xs]); setBanner(`${u.displayName}'s account was created.`); }} />

      {adding && (
        <AddOfficeUserForm
          onCancel={() => setAdding(false)}
          onAdded={(u) => {
            setUsers((xs) => [...xs, u]);
            setAdding(false);
            setBanner(`${u.displayName} was added. They can now sign in with Google using ${u.email ?? "their address"}.`);
          }}
        />
      )}

      {banner && (
        <div role="status" className="mb-4 flex items-start justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <p className="text-sm text-ele-body">{banner}</p>
          <button type="button" onClick={() => setBanner(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {!loading && !listError && users.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search name, email or role..."
          searchLabel="Search office users"
          resultCount={{ shown: visible.length, total: users.length, noun: "office users" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {loading ? (
        <Loader label="Loading the office users..." />
      ) : listError ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the office users. Please try again in a moment.
        </p>
      ) : users.length === 0 ? (
        <EmptyState message="No office users yet. Add one above, then they can sign in with Google." />
      ) : visible.length === 0 ? (
        <EmptyState role="status" message={`No office users match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Session ALY mobile card conversion: the desktop table's surviving mobile columns (Name +
        // Role + the enable/disable action) still divide one phone width between them, and Delete
        // is dropped entirely below md. The card stacks instead: Email comes back as a second
        // identifying line, Status and Role sit together on the right, and Delete returns too since
        // the card has room for a third control (officeUserCardActions above).
        <MobileCardList label="Office users">
          {visible.map((u) => (
            <MobileListCard
              key={u.id}
              primary={
                <>
                  {u.displayName}
                  {u.isSelf && <span className="ml-2 font-menu text-[0.65rem] uppercase tracking-wide text-ele-blue">You</span>}
                </>
              }
              secondary={
                <>
                  {u.email ?? "-"}
                  {settingPasswordFor === u.id && (
                    <SetPasswordForm
                      user={u}
                      onDone={() => {
                        setSettingPasswordFor(null);
                        setBanner(passwordSetMessage(u));
                      }}
                      onCancel={() => setSettingPasswordFor(null)}
                    />
                  )}
                </>
              }
              right={
                <>
                  <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{u.role}</span>
                  {statusTag(u)}
                </>
              }
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => setSettingPasswordFor(settingPasswordFor === u.id ? null : u.id)}
                    className="min-h-11 flex-1 rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  >
                    Set password
                  </button>
                  {!u.isSelf && officeUserCardActions(u, busyId, openDeactivate, reactivate, openDelete)}
                  {!u.isSelf && u.isActive && <SignOutEverywhereButton userId={u.id} name={u.displayName} className="min-h-11 flex-1" />}
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* No detail page: below md the budget is Name (identifier) + Role + the single
                enable/disable action (which also implies the current Status, since a visible
                Deactivate button means active and vice versa). Email, the separate Status column
                and the permanent Delete are dropped on a small screen (rule 4). */}
            <colgroup>
              <col style={{ width: "24%" }} />
              <col className="max-md:hidden" style={{ width: "28%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col style={{ width: "24%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Name" colKey="name" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Email" colKey="email" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Role" colKey="role" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="cell-actions px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <tr key={u.id} className={`border-t border-ele-purple-accent ${u.isActive ? "" : "opacity-70"}`}>
                  <td className="px-3 py-2 align-top text-ele-strong">
                    {u.displayName}
                    {u.isSelf && <span className="ml-2 font-menu text-[0.65rem] uppercase tracking-wide text-ele-blue">You</span>}
                    {settingPasswordFor === u.id && (
                      <SetPasswordForm
                        user={u}
                        onDone={() => {
                          setSettingPasswordFor(null);
                          setBanner(passwordSetMessage(u));
                        }}
                        onCancel={() => setSettingPasswordFor(null)}
                      />
                    )}
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top">{u.email ?? "-"}</td>
                  <td className="px-3 py-2 align-top">
                    <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{u.role}</span>
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top">
                    {statusTag(u)}
                  </td>
                  <td className="cell-actions px-3 py-2 text-right align-top">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setSettingPasswordFor(settingPasswordFor === u.id ? null : u.id)}
                        className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green"
                      >
                        Set password
                      </button>
                      {u.isSelf ? null : (
                      <>
                        {u.isActive && <SignOutEverywhereButton userId={u.id} name={u.displayName} />}
                        {u.isActive ? (
                          <button
                            type="button"
                            disabled={busyId === u.id}
                            onClick={() => {
                              setModalError(null);
                              setPending({ user: u, action: "deactivate" });
                            }}
                            className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-amber-500 hover:text-amber-400 disabled:opacity-50"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busyId === u.id}
                            onClick={() => reactivate(u)}
                            className="rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
                          >
                            {busyId === u.id ? "..." : "Reactivate"}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busyId === u.id}
                          onClick={() => {
                            setModalError(null);
                            setPending({ user: u, action: "delete" });
                          }}
                          className="max-md:hidden rounded-ele border border-ele-purple-accent px-2.5 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDeleteModal
        open={pending !== null}
        variant={pending?.action === "delete" ? "destructive" : "archive"}
        title={
          pending?.action === "delete"
            ? `Delete ${pending?.user.displayName}?`
            : `Deactivate ${pending?.user.displayName}?`
        }
        message={
          pending?.action === "delete"
            ? "This permanently removes the account. The person can no longer sign in. Past activity stays in the audit trail. This cannot be undone."
            : "The account is kept but can no longer sign in. You can reactivate it later."
        }
        confirmLabel={pending?.action === "delete" ? "Delete user" : "Deactivate"}
        busy={busyId !== null && pending !== null}
        error={modalError}
        onConfirm={confirmPending}
        onCancel={() => {
          if (busyId !== null) return;
          setPending(null);
          setModalError(null);
        }}
      />
    </div>
  );
}
